import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Progress, Tabs, List, Tag, Button, Select } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined, ExclamationCircleOutlined, ClockCircleOutlined, CheckCircleOutlined } from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import { MapContainer, TileLayer, Circle, Tooltip, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import './index.css';

const { TabPane } = Tabs;
const { Option } = Select;

// 修复Leaflet默认图标问题
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// 模拟数据
const mockStats = {
  score: 87.6,
  change: 2.3,
  warningCount: {
    urgent: 5,
    high: 12,
    normal: 24
  },
  processedTasks: 156,
  pendingTasks: 43,
  overdueTasks: 7
};

const mockRegions = [
  { name: '城东区', score: 92, lat: 39.9, lng: 116.4, population: 200000 },
  { name: '西湖区', score: 86, lat: 39.92, lng: 116.38, population: 350000 },
  { name: '河北区', score: 78, lat: 39.95, lng: 116.42, population: 180000 },
  { name: '南山区', score: 89, lat: 39.88, lng: 116.37, population: 420000 },
  { name: '江北区', score: 81, lat: 39.91, lng: 116.46, population: 250000 },
];

const mockWarnings = [
  { id: 1, type: '指标异常', area: '城东区', level: 'urgent', title: '企业开办时间突增', time: '10分钟前' },
  { id: 2, type: '投诉反馈', area: '西湖区', level: 'high', title: '多家企业反映行政审批延迟', time: '30分钟前' },
  { id: 3, type: '舆情监测', area: '全市', level: 'normal', title: '社交媒体出现负面评价讨论', time: '1小时前' },
  { id: 4, type: '系统预警', area: '河北区', level: 'high', title: '营商环境指数下降趋势明显', time: '2小时前' },
  { id: 5, type: '投诉反馈', area: '南山区', level: 'normal', title: '企业融资难问题集中反映', time: '3小时前' },
];

const Dashboard = () => {
  const [timeRange, setTimeRange] = useState('month');
  
  // 环形进度条配置
  const scoreStyle = {
    strokeColor: {
      '0%': '#108ee9',
      '100%': '#87d068',
    },
    strokeWidth: 10,
    trailColor: '#f5f5f5',
  };
  
  // 地图配置
  const mapCenter = [39.9, 116.4];
  const mapZoom = 11;
  
  // 折线图配置
  const lineChartOption = {
    title: {
      text: '近6个月核心指标趋势',
      left: 'center'
    },
    tooltip: {
      trigger: 'axis'
    },
    legend: {
      data: ['企业开办时长', '融资成本', '满意度'],
      bottom: 0
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '10%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: ['1月', '2月', '3月', '4月', '5月', '6月']
    },
    yAxis: {
      type: 'value'
    },
    series: [
      {
        name: '企业开办时长',
        type: 'line',
        data: [6.2, 5.9, 5.8, 4.5, 4.2, 3.8],
        smooth: true,
        areaStyle: {
          opacity: 0.1
        }
      },
      {
        name: '融资成本',
        type: 'line',
        data: [8.2, 8.0, 7.5, 7.6, 7.2, 6.8],
        smooth: true,
        areaStyle: {
          opacity: 0.1
        }
      },
      {
        name: '满意度',
        type: 'line',
        data: [82, 83, 85, 86, 89, 92],
        smooth: true,
        areaStyle: {
          opacity: 0.1
        }
      }
    ]
  };
  
  // 任务处理统计卡片
  const taskStatsOption = {
    tooltip: {
      trigger: 'item'
    },
    legend: {
      orient: 'vertical',
      left: 'left',
    },
    series: [
      {
        type: 'pie',
        radius: ['40%', '70%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 10,
          borderColor: '#fff',
          borderWidth: 2
        },
        label: {
          show: false,
          position: 'center'
        },
        emphasis: {
          label: {
            show: true,
            fontSize: '18',
            fontWeight: 'bold'
          }
        },
        labelLine: {
          show: false
        },
        data: [
          { value: mockStats.processedTasks, name: '已办结' },
          { value: mockStats.pendingTasks, name: '处理中' },
          { value: mockStats.overdueTasks, name: '已超时' }
        ]
      }
    ]
  };
  
  // 根据预警级别返回标签颜色
  const getTagColor = (level) => {
    const colors = {
      urgent: 'red',
      high: 'orange',
      normal: 'blue'
    };
    return colors[level] || 'blue';
  };
  
  // 获取各指标名称和值
  const getIndicatorTitle = (score) => {
    if (score >= 90) return '优秀';
    if (score >= 80) return '良好';
    if (score >= 70) return '一般';
    if (score >= 60) return '较差';
    return '差';
  };
  
  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>全景监测驾驶舱</h1>
        <div className="dashboard-filter">
          <span>数据周期：</span>
          <Select value={timeRange} onChange={setTimeRange} style={{ width: 120 }}>
            <Option value="week">本周</Option>
            <Option value="month">本月</Option>
            <Option value="quarter">本季度</Option>
            <Option value="year">本年度</Option>
          </Select>
        </div>
      </div>
      
      <Row gutter={[16, 16]}>
        {/* 左侧栏 - 核心指标 */}
        <Col xs={24} lg={6}>
          <Card className="dashboard-card" title="营商环境健康指数">
            <div className="dashboard-score">
              <Progress 
                type="dashboard" 
                percent={mockStats.score} 
                format={(percent) => <span className="score-value">{percent}</span>} 
                strokeColor={scoreStyle.strokeColor} 
                strokeWidth={scoreStyle.strokeWidth} 
                trailColor={scoreStyle.trailColor}
              />
              <div className="score-status">
                <span className="score-title">{getIndicatorTitle(mockStats.score)}</span>
                <span className="score-change">
                  {mockStats.change >= 0 ? (
                    <ArrowUpOutlined style={{ color: '#52c41a' }} />
                  ) : (
                    <ArrowDownOutlined style={{ color: '#f5222d' }} />
                  )}
                  {Math.abs(mockStats.change)}%
                </span>
              </div>
            </div>
          </Card>
          
          <Card className="dashboard-card" title="今日预警总数">
            <div className="warning-stats">
              <div className="warning-item urgent">
                <Tag color="red" className="warning-tag">紧急</Tag>
                <span className="warning-count">{mockStats.warningCount.urgent}</span>
              </div>
              <div className="warning-item high">
                <Tag color="orange" className="warning-tag">重要</Tag>
                <span className="warning-count">{mockStats.warningCount.high}</span>
              </div>
              <div className="warning-item normal">
                <Tag color="blue" className="warning-tag">一般</Tag>
                <span className="warning-count">{mockStats.warningCount.normal}</span>
              </div>
            </div>
          </Card>
          
          <Card className="dashboard-card" title="诉求处理看板">
            <ReactECharts 
              option={taskStatsOption} 
              style={{ height: '250px' }}
            />
            <div className="task-stats">
              <Statistic 
                title="已办结" 
                value={mockStats.processedTasks} 
                valueStyle={{ color: '#3f8600' }}
                prefix={<CheckCircleOutlined />}
              />
              <Statistic 
                title="处理中" 
                value={mockStats.pendingTasks} 
                valueStyle={{ color: '#1890ff' }}
                prefix={<ClockCircleOutlined />}
              />
              <Statistic 
                title="已超时" 
                value={mockStats.overdueTasks} 
                valueStyle={{ color: '#cf1322' }}
                prefix={<ExclamationCircleOutlined />}
              />
            </div>
          </Card>
        </Col>
        
        {/* 中间栏 - 数据可视化 */}
        <Col xs={24} lg={12}>
          <Card className="dashboard-card map-card" title="区域营商环境指数">
            <MapContainer center={mapCenter} zoom={mapZoom} style={{ height: '400px' }}>
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {mockRegions.map((region) => (
                <Circle
                  key={region.name}
                  center={[region.lat, region.lng]}
                  radius={region.population / 10}
                  pathOptions={{
                    fillColor: region.score >= 90 ? '#52c41a' : 
                               region.score >= 80 ? '#1890ff' : 
                               region.score >= 70 ? '#faad14' : '#f5222d',
                    fillOpacity: 0.6,
                    weight: 1,
                    color: '#fff'
                  }}
                >
                  <Popup>
                    <div>
                      <h3>{region.name}</h3>
                      <p>营商环境指数: {region.score}</p>
                      <p>评级: {getIndicatorTitle(region.score)}</p>
                    </div>
                  </Popup>
                </Circle>
              ))}
            </MapContainer>
          </Card>
          
          <Card className="dashboard-card">
            <ReactECharts
              option={lineChartOption}
              style={{ height: '300px' }}
            />
          </Card>
        </Col>
        
        {/* 右侧栏 - 预警快讯 */}
        <Col xs={24} lg={6}>
          <Card 
            className="dashboard-card warning-card" 
            title="预警快讯" 
            extra={<Button type="link">查看全部</Button>}
          >
            <Tabs defaultActiveKey="all">
              <TabPane tab="全部" key="all">
                <List
                  className="warning-list"
                  itemLayout="horizontal"
                  dataSource={mockWarnings}
                  renderItem={item => (
                    <List.Item>
                      <div className="warning-list-item">
                        <div className="warning-list-header">
                          <Tag color={getTagColor(item.level)}>{item.type}</Tag>
                          <span className="warning-time">{item.time}</span>
                        </div>
                        <div className="warning-list-content">
                          <h4>{item.title}</h4>
                          <div className="warning-area">{item.area}</div>
                        </div>
                        <div className="warning-list-footer">
                          <Button type="link" size="small">处理</Button>
                          <Button type="text" size="small">详情</Button>
                        </div>
                      </div>
                    </List.Item>
                  )}
                />
              </TabPane>
              <TabPane tab="紧急" key="urgent">
                <List
                  className="warning-list"
                  itemLayout="horizontal"
                  dataSource={mockWarnings.filter(w => w.level === 'urgent')}
                  renderItem={item => (
                    <List.Item>
                      <div className="warning-list-item">
                        <div className="warning-list-header">
                          <Tag color={getTagColor(item.level)}>{item.type}</Tag>
                          <span className="warning-time">{item.time}</span>
                        </div>
                        <div className="warning-list-content">
                          <h4>{item.title}</h4>
                          <div className="warning-area">{item.area}</div>
                        </div>
                        <div className="warning-list-footer">
                          <Button type="link" size="small">处理</Button>
                          <Button type="text" size="small">详情</Button>
                        </div>
                      </div>
                    </List.Item>
                  )}
                />
              </TabPane>
              <TabPane tab="重要" key="high">
                <List
                  className="warning-list"
                  itemLayout="horizontal"
                  dataSource={mockWarnings.filter(w => w.level === 'high')}
                  renderItem={item => (
                    <List.Item>
                      <div className="warning-list-item">
                        <div className="warning-list-header">
                          <Tag color={getTagColor(item.level)}>{item.type}</Tag>
                          <span className="warning-time">{item.time}</span>
                        </div>
                        <div className="warning-list-content">
                          <h4>{item.title}</h4>
                          <div className="warning-area">{item.area}</div>
                        </div>
                        <div className="warning-list-footer">
                          <Button type="link" size="small">处理</Button>
                          <Button type="text" size="small">详情</Button>
                        </div>
                      </div>
                    </List.Item>
                  )}
                />
              </TabPane>
            </Tabs>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard; 